import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { BodyOf, ResponseOf } from "../../../../../core/api";
import useApi from "../../../../../shared/hooks/useApi";
import useAgencyMembers from "../../../../../shared/hooks/useAgencyMembers";
import type {
  PatientContractId,
  PatientId,
  PatientSurplusInvoiceId,
} from "../../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { EntityProfileEmptyState } from "../../../../entityProfileModal/components/EntityProfileFeedback";
import {
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../entityProfileModal/components/EntityProfileSummary";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  contracts: PatientProfilePatient["contracts"];
  patientId: PatientId;
};

type PatientSurplus = ResponseOf<
  "get",
  "./patients/:patientId/patient_surpluses"
>["patientSurpluses"][number];
type PatientSurplusCheck = ResponseOf<
  "get",
  "./patients/:patientId/patient_surplus_checks"
>["patientSurplusChecks"][number];
type PatientSurplusInvoice = ResponseOf<
  "get",
  "./patients/:patientId/patient_surplus_invoices"
>["patientSurplusInvoices"][number];
type PatientSurplusCreateBody = BodyOf<"post", "./patients/:patientId/patient_surpluses">;
type PatientSurplusCheckCreateBody = BodyOf<"post", "./patients/:patientId/patient_surplus_checks">;
type PatientSurplusInvoiceCreateBody = BodyOf<
  "post",
  "./patients/:patientId/patient_surplus_invoices"
>;
type InvoiceCreateResponse = ResponseOf<"post", "./patients/:patientId/patient_surplus_invoices">;

type ModalProps = {
  isOpen: boolean;
  isSaving: boolean;
  onClose: () => void;
};

const patientSurplusQueryKeys = {
  all: (patientId: PatientProfilePatient["id"]) => ["patientProfileModal", patientId, "surpluses"],
  current: (patientId: PatientProfilePatient["id"], date: LocalDate) => [
    "patientProfileModal",
    patientId,
    "surpluses",
    "current",
    date.toString(),
  ],
  checks: (patientId: PatientProfilePatient["id"]) => [
    "patientProfileModal",
    patientId,
    "surplusChecks",
  ],
  invoices: (patientId: PatientProfilePatient["id"]) => [
    "patientProfileModal",
    patientId,
    "surplusInvoices",
  ],
};

function formatDate(value: LocalDate | null | undefined) {
  return value === null || value === undefined ? "" : dateFormatter.toDate(value);
}

function formatDateTime(value: PatientSurplus["createdAt"] | PatientSurplusCheck["createdAt"]) {
  return dateFormatter.toDateTime(value);
}

function formatCents(value: number | null | undefined) {
  return value === null || value === undefined ? "" : currencyFormatter.formatCents(value);
}

function getContractLabel(
  contracts: PatientProfilePatient["contracts"],
  patientContractId: PatientSurplus["patientContractId"]
) {
  const contract = contracts.find((item) => item.id === patientContractId);

  if (contract === undefined) {
    return patientContractId === null ? "" : `Contract #${patientContractId}`;
  }

  const start = formatDate(contract.startDate);
  const end = formatDate(contract.endDate);
  const dates = [start, end].filter(Boolean).join(" - ");

  return dates === "" ? contract.contractTypeName : `${contract.contractTypeName} (${dates})`;
}

function getAgencyMemberName(
  agencyMembers: ReturnType<typeof useAgencyMembers>["data"],
  agencyMemberId: number
) {
  const agencyMember = agencyMembers?.find((member) => member.id === agencyMemberId);

  if (agencyMember === undefined) {
    return `User #${agencyMemberId}`;
  }

  return `${agencyMember.firstName} ${agencyMember.lastName}`;
}

function getSortedByIdDesc<T extends { id: number }>(items: T[] | undefined) {
  return [...(items ?? [])].sort((a, b) => b.id - a.id);
}

function getInvoiceBalanceAmount(invoice: PatientSurplusInvoice) {
  return invoice.totalAmountInCents - invoice.patientSurplusAmountInCents;
}

function getInvoiceOutstandingBalance(invoice: PatientSurplusInvoice) {
  return invoice.outstandingSumInCents;
}

function getInvoiceMonths(fromDate: string, toDate: string) {
  const months: LocalDate[] = [];
  const start = LocalDate.parse(fromDate).withDayOfMonth(1);
  const end = LocalDate.parse(toDate).withDayOfMonth(1);
  let current = start;

  while (current.isBefore(end) || current.isEqual(end)) {
    months.push(current);
    current = current.plusMonths(1);
  }

  return months;
}

function DataAlert(props: {
  isError: boolean;
  isLoading: boolean;
  label: string;
  onRetry: () => void;
}) {
  if (props.isLoading) {
    return (
      <HStack color="#64748b" py={4}>
        <Spinner size="sm" />
        <Text fontSize="14px">Loading {props.label}</Text>
      </HStack>
    );
  }

  if (props.isError) {
    return (
      <Alert borderRadius="4px" status="error">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle fontSize="14px">Unable to load {props.label}.</AlertTitle>
          <AlertDescription fontSize="13px">Retry to reload this surplus data.</AlertDescription>
        </Box>
        <EntityModalButton kind="white" size="sm" onClick={props.onRetry}>
          Retry
        </EntityModalButton>
      </Alert>
    );
  }

  return null;
}

function SectionHeader(props: { children: React.ReactNode; action: React.ReactNode }) {
  return (
    <HStack alignItems="center" justifyContent="space-between" spacing={3}>
      <Text color="#0f172a" fontSize="14px" fontWeight={700}>
        {props.children}
      </Text>
      {props.action}
    </HStack>
  );
}

function SurplusCreateModal(
  props: ModalProps & {
    contracts: PatientProfilePatient["contracts"];
    onSubmit: (body: PatientSurplusCreateBody) => void;
  }
) {
  const today = LocalDate.now().toString();
  const [fromDate, setFromDate] = React.useState(today);
  const [toDate, setToDate] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [patientContractId, setPatientContractId] = React.useState("");
  const activeContracts = props.contracts.filter(
    (contract) => contract.endDate === null || !contract.endDate.isBefore(LocalDate.now())
  );
  const showErrors =
    fromDate === "" || amount === "" || Number(amount) < 0 || patientContractId === "";

  React.useEffect(() => {
    if (props.isOpen) {
      setFromDate(today);
      setToDate("");
      setAmount("");
      setPatientContractId("");
    }
  }, [props.isOpen, today]);

  const submit = () => {
    if (showErrors) {
      return;
    }

    props.onSubmit({
      amountInCents: Math.round(Number(amount) * 100),
      fromDate: LocalDate.parse(fromDate),
      patientContractId: Number(patientContractId) as PatientContractId,
      toDate: toDate === "" ? null : LocalDate.parse(toDate),
    });
  };

  return (
    <Modal isOpen={props.isOpen} size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add patient surplus</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack alignItems="stretch" spacing={4}>
            <Grid gap={4} templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}>
              <FormControl isRequired>
                <FormLabel>From</FormLabel>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(event) => setFromDate(event.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>To</FormLabel>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(event) => setToDate(event.target.value)}
                />
              </FormControl>
            </Grid>
            <FormControl isRequired isInvalid={amount !== "" && Number(amount) < 0}>
              <FormLabel>Surplus amount</FormLabel>
              <Input
                min={0}
                step="0.01"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <FormErrorMessage>Amount must be zero or greater.</FormErrorMessage>
            </FormControl>
            <FormControl
              isRequired
              isInvalid={props.contracts.length > 0 && patientContractId === ""}
            >
              <FormLabel>Contract</FormLabel>
              <Select
                placeholder={
                  activeContracts.length === 0 ? "No active contracts" : "Select contract"
                }
                value={patientContractId}
                onChange={(event) => setPatientContractId(event.target.value)}
              >
                {activeContracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {getContractLabel(props.contracts, contract.id)}
                  </option>
                ))}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <EntityModalButton kind="white" onClick={props.onClose}>
              Cancel
            </EntityModalButton>
            <EntityModalButton
              disabled={showErrors}
              isLoading={props.isSaving}
              kind="blue"
              onClick={submit}
            >
              Save
            </EntityModalButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function SurplusCheckCreateModal(
  props: ModalProps & {
    onSubmit: (body: PatientSurplusCheckCreateBody) => void;
    patientId: PatientProfilePatient["id"];
  }
) {
  const today = LocalDate.now().toString();
  const [receivedAt, setReceivedAt] = React.useState(today);
  const [amount, setAmount] = React.useState("");
  const showErrors = receivedAt === "" || amount === "" || Number(amount) < 0;

  React.useEffect(() => {
    if (props.isOpen) {
      setReceivedAt(today);
      setAmount("");
    }
  }, [props.isOpen, today]);

  const submit = () => {
    if (showErrors) {
      return;
    }

    props.onSubmit({
      patientId: props.patientId as PatientId,
      receivedAt: LocalDate.parse(receivedAt),
      totalAmountInCents: Math.round(Number(amount) * 100),
    });
  };

  return (
    <Modal isOpen={props.isOpen} size="md" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add patient surplus check</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack alignItems="stretch" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Received date</FormLabel>
              <Input
                type="date"
                value={receivedAt}
                onChange={(event) => setReceivedAt(event.target.value)}
              />
            </FormControl>
            <FormControl isRequired isInvalid={amount !== "" && Number(amount) < 0}>
              <FormLabel>Check amount</FormLabel>
              <Input
                min={0}
                step="0.01"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <FormErrorMessage>Amount must be zero or greater.</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <EntityModalButton kind="white" onClick={props.onClose}>
              Cancel
            </EntityModalButton>
            <EntityModalButton
              disabled={showErrors}
              isLoading={props.isSaving}
              kind="blue"
              onClick={submit}
            >
              Save
            </EntityModalButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function SurplusInvoiceCreateModal(
  props: ModalProps & {
    onSubmit: (body: PatientSurplusInvoiceCreateBody) => void;
    patientId: PatientProfilePatient["id"];
  }
) {
  const currentMonth = LocalDate.now().withDayOfMonth(1).toString();
  const [fromMonth, setFromMonth] = React.useState(currentMonth);
  const [toMonth, setToMonth] = React.useState(currentMonth);
  const showErrors =
    fromMonth === "" ||
    toMonth === "" ||
    LocalDate.parse(toMonth).isBefore(LocalDate.parse(fromMonth));

  React.useEffect(() => {
    if (props.isOpen) {
      setFromMonth(currentMonth);
      setToMonth(currentMonth);
    }
  }, [currentMonth, props.isOpen]);

  const submit = () => {
    if (showErrors) {
      return;
    }

    props.onSubmit({
      datesArr: getInvoiceMonths(fromMonth, toMonth),
      patientId: props.patientId as PatientId,
    });
  };

  return (
    <Modal isOpen={props.isOpen} size="lg" onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add patient surplus invoice</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid gap={4} templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}>
            <FormControl isRequired>
              <FormLabel>From month</FormLabel>
              <Input
                type="month"
                value={fromMonth.slice(0, 7)}
                onChange={(event) =>
                  setFromMonth(event.target.value === "" ? "" : `${event.target.value}-01`)
                }
              />
            </FormControl>
            <FormControl isRequired isInvalid={showErrors}>
              <FormLabel>To month</FormLabel>
              <Input
                type="month"
                value={toMonth.slice(0, 7)}
                onChange={(event) =>
                  setToMonth(event.target.value === "" ? "" : `${event.target.value}-01`)
                }
              />
              <FormErrorMessage>End month must be on or after start month.</FormErrorMessage>
            </FormControl>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <EntityModalButton kind="white" onClick={props.onClose}>
              Cancel
            </EntityModalButton>
            <EntityModalButton
              disabled={showErrors}
              isLoading={props.isSaving}
              kind="blue"
              onClick={submit}
            >
              Save
            </EntityModalButton>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function InvoiceCreateResultAlert(props: { response: InvoiceCreateResponse | null }) {
  if (props.response === null) {
    return null;
  }

  return (
    <Alert borderRadius="4px" status="info">
      <AlertIcon />
      <Box flex="1">
        <AlertTitle fontSize="14px">Invoice create result</AlertTitle>
        <AlertDescription fontSize="13px">
          {props.response.patientSurplusInvoiceCreateResponseRecords
            .map((record) => `${formatDate(record.month)} - ${record.result}`)
            .join(", ")}
        </AlertDescription>
      </Box>
    </Alert>
  );
}

export default function PatientProfileSurplusesSection(props: Props) {
  const { api } = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const agencyMembersQuery = useAgencyMembers();
  const surplusModal = useDisclosure();
  const checkModal = useDisclosure();
  const invoiceModal = useDisclosure();
  const today = React.useMemo(() => LocalDate.now(), []);
  const [invoiceCreateResponse, setInvoiceCreateResponse] =
    React.useState<InvoiceCreateResponse | null>(null);
  const patientId = props.patientId;

  const surplusesQuery = useQuery({
    queryKey: patientSurplusQueryKeys.all(patientId),
    queryFn: () =>
      api.get("./patients/:patientId/patient_surpluses", {
        path: { patientId },
        query: {},
      }),
  });
  const currentSurplusQuery = useQuery({
    queryKey: patientSurplusQueryKeys.current(patientId, today),
    queryFn: () =>
      api.get("./patients/:patientId/patient_surpluses", {
        path: { patientId },
        query: { surplusDate: today },
      }),
  });
  const invoicesQuery = useQuery({
    queryKey: patientSurplusQueryKeys.invoices(patientId),
    queryFn: () =>
      api.get("./patients/:patientId/patient_surplus_invoices", {
        path: { patientId },
        query: {},
      }),
  });
  const checksQuery = useQuery({
    queryKey: patientSurplusQueryKeys.checks(patientId),
    queryFn: () =>
      api.get("./patients/:patientId/patient_surplus_checks", {
        path: { patientId },
      }),
  });

  const invalidateSurplusQueries = () => {
    void queryClient.invalidateQueries({ queryKey: patientSurplusQueryKeys.all(patientId) });
    void queryClient.invalidateQueries({
      queryKey: patientSurplusQueryKeys.current(patientId, today),
    });
    void queryClient.invalidateQueries({ queryKey: patientSurplusQueryKeys.invoices(patientId) });
    void queryClient.invalidateQueries({ queryKey: patientSurplusQueryKeys.checks(patientId) });
  };

  const createSurplusMutation = useMutation({
    mutationFn: (body: PatientSurplusCreateBody) =>
      api.post("./patients/:patientId/patient_surpluses", { path: { patientId }, body }),
    onSuccess: () => {
      toast({ status: "success", title: "Surplus created" });
      surplusModal.onClose();
      invalidateSurplusQueries();
    },
    onError: () => {
      toast({ status: "error", title: "Unable to create surplus" });
    },
  });

  const createCheckMutation = useMutation({
    mutationFn: (body: PatientSurplusCheckCreateBody) =>
      api.post("./patients/:patientId/patient_surplus_checks", { path: { patientId }, body }),
    onSuccess: () => {
      toast({ status: "success", title: "Surplus check created" });
      checkModal.onClose();
      invalidateSurplusQueries();
    },
    onError: () => {
      toast({ status: "error", title: "Unable to create surplus check" });
    },
  });

  const createInvoiceMutation = useMutation({
    mutationFn: (body: PatientSurplusInvoiceCreateBody) =>
      api.post("./patients/:patientId/patient_surplus_invoices", { path: { patientId }, body }),
    onSuccess: (response) => {
      toast({ status: "success", title: "Surplus invoice created" });
      setInvoiceCreateResponse(response);
      invoiceModal.onClose();
      invalidateSurplusQueries();
    },
    onError: () => {
      toast({ status: "error", title: "Unable to create surplus invoice" });
    },
  });

  const openInvoicePdfMutation = useMutation({
    mutationFn: (invoiceId: PatientSurplusInvoiceId) =>
      api.get("./patients/:patientId/:invoiceId/pdf", {
        path: { patientId, invoiceId },
      }),
    onSuccess: (response) => {
      window.open(response.url, "_blank", "noopener,noreferrer");
    },
    onError: () => {
      toast({ status: "error", title: "Unable to open surplus invoice PDF" });
    },
  });

  const currentSurplus = currentSurplusQuery.data?.patientSurpluses[0] ?? null;
  const sortedSurpluses = getSortedByIdDesc(surplusesQuery.data?.patientSurpluses);
  const sortedInvoices = getSortedByIdDesc(invoicesQuery.data?.patientSurplusInvoices);
  const sortedChecks = getSortedByIdDesc(checksQuery.data?.patientSurplusChecks);
  const invoiceRows = invoicesQuery.data?.patientSurplusInvoices ?? [];
  const currentTotalBalance =
    invoiceRows.length === 0
      ? null
      : getInvoiceOutstandingBalance(invoiceRows[invoiceRows.length - 1]);

  return (
    <VStack alignItems="stretch" spacing={6}>
      <Grid gap={4} templateColumns={{ base: "1fr", xl: "repeat(2, 1fr)" }}>
        <SummaryBlock title={`Current balance as of ${formatDate(today)}`}>
          <SummaryRow label="Current total balance" value={formatCents(currentTotalBalance)} />
        </SummaryBlock>
        <SummaryBlock title="Current monthly surplus">
          <SummaryRow
            label="Amount"
            value={currentSurplus === null ? "" : formatCents(currentSurplus.amountInCents)}
          />
          <SummaryRow
            label="Contract"
            value={
              currentSurplus === null
                ? ""
                : getContractLabel(props.contracts, currentSurplus.patientContractId)
            }
          />
        </SummaryBlock>
      </Grid>

      <DataAlert
        isError={surplusesQuery.isError || currentSurplusQuery.isError}
        isLoading={surplusesQuery.isLoading || currentSurplusQuery.isLoading}
        label="surpluses"
        onRetry={() => {
          void surplusesQuery.refetch();
          void currentSurplusQuery.refetch();
        }}
      />

      <VStack alignItems="stretch" spacing={3}>
        <SectionHeader
          action={
            <HStack spacing={2}>
              <EntityModalButton
                iconClassName="fa fa-plus"
                kind="blue"
                size="sm"
                onClick={surplusModal.onOpen}
              >
                Add Surplus
              </EntityModalButton>
            </HStack>
          }
        >
          Surpluses
        </SectionHeader>
        {surplusesQuery.isLoading || surplusesQuery.isError ? null : sortedSurpluses.length ===
          0 ? (
          <EntityProfileEmptyState>No patient surplus found.</EntityProfileEmptyState>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>From</Th>
                  <Th>To</Th>
                  <Th>Contract</Th>
                  <Th isNumeric>Amount</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedSurpluses.map((surplus) => (
                  <Tr key={surplus.id}>
                    <Td>{surplus.id}</Td>
                    <Td>{formatDate(surplus.fromDate)}</Td>
                    <Td>{formatDate(surplus.toDate)}</Td>
                    <Td>{getContractLabel(props.contracts, surplus.patientContractId)}</Td>
                    <Td isNumeric>{formatCents(surplus.amountInCents)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      <InvoiceCreateResultAlert response={invoiceCreateResponse} />

      <VStack alignItems="stretch" spacing={3}>
        <SectionHeader
          action={
            <HStack spacing={2}>
              <EntityModalButton
                iconClassName="fa fa-plus"
                kind="blue"
                size="sm"
                onClick={invoiceModal.onOpen}
              >
                Add surplus invoice
              </EntityModalButton>
            </HStack>
          }
        >
          Invoices
        </SectionHeader>
        <DataAlert
          isError={invoicesQuery.isError}
          isLoading={invoicesQuery.isLoading}
          label="surplus invoices"
          onRetry={() => void invoicesQuery.refetch()}
        />
        {invoicesQuery.isLoading || invoicesQuery.isError ? null : sortedInvoices.length === 0 ? (
          <EntityProfileEmptyState>No patient surplus invoices.</EntityProfileEmptyState>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Invoice date</Th>
                  <Th isNumeric>Total amount</Th>
                  <Th isNumeric>Balance amount</Th>
                  <Th isNumeric>Surplus amount</Th>
                  <Th isNumeric>Paid amount</Th>
                  <Th isNumeric>Outstanding balance</Th>
                  <Th>Check IDs</Th>
                  <Th>PDF</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedInvoices.map((invoice) => (
                  <Tr key={invoice.id}>
                    <Td>{invoice.id}</Td>
                    <Td>{formatDate(invoice.invoiceDate)}</Td>
                    <Td isNumeric>{formatCents(invoice.totalAmountInCents)}</Td>
                    <Td isNumeric>{formatCents(getInvoiceBalanceAmount(invoice))}</Td>
                    <Td isNumeric>{formatCents(invoice.patientSurplusAmountInCents)}</Td>
                    <Td isNumeric>{formatCents(invoice.paidAmountInCents)}</Td>
                    <Td isNumeric>{formatCents(getInvoiceOutstandingBalance(invoice))}</Td>
                    <Td>{(invoice.patientSurplusCheckIds ?? []).join(", ")}</Td>
                    <Td>
                      <EntityModalButton
                        iconClassName="fa fa-external-link"
                        isLoading={openInvoicePdfMutation.isPending}
                        kind="white"
                        size="xs"
                        onClick={() => openInvoicePdfMutation.mutate(invoice.id)}
                      >
                        Get Invoice
                      </EntityModalButton>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      <VStack alignItems="stretch" spacing={3}>
        <SectionHeader
          action={
            <HStack spacing={2}>
              <EntityModalButton
                iconClassName="fa fa-plus"
                kind="blue"
                size="sm"
                onClick={checkModal.onOpen}
              >
                Add Surplus Check
              </EntityModalButton>
            </HStack>
          }
        >
          Surplus checks
        </SectionHeader>
        <DataAlert
          isError={checksQuery.isError}
          isLoading={checksQuery.isLoading}
          label="surplus checks"
          onRetry={() => void checksQuery.refetch()}
        />
        {checksQuery.isLoading || checksQuery.isError ? null : sortedChecks.length === 0 ? (
          <EntityProfileEmptyState>No patient surplus checks found.</EntityProfileEmptyState>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>ID</Th>
                  <Th>Recieved At</Th>
                  <Th isNumeric>Amount</Th>
                  <Th>Created At</Th>
                  <Th>Created By</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedChecks.map((check) => (
                  <Tr key={check.id}>
                    <Td>{check.id}</Td>
                    <Td>{formatDate(check.receivedAt)}</Td>
                    <Td isNumeric>{formatCents(check.totalAmountInCents)}</Td>
                    <Td>{formatDateTime(check.createdAt)}</Td>
                    <Td>{getAgencyMemberName(agencyMembersQuery.data, check.createdBy)}</Td>
                    <Td />
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </VStack>

      <SurplusCreateModal
        contracts={props.contracts}
        isOpen={surplusModal.isOpen}
        isSaving={createSurplusMutation.isPending}
        onClose={surplusModal.onClose}
        onSubmit={(body) => createSurplusMutation.mutate(body)}
      />
      <SurplusCheckCreateModal
        isOpen={checkModal.isOpen}
        isSaving={createCheckMutation.isPending}
        patientId={patientId}
        onClose={checkModal.onClose}
        onSubmit={(body) => createCheckMutation.mutate(body)}
      />
      <SurplusInvoiceCreateModal
        isOpen={invoiceModal.isOpen}
        isSaving={createInvoiceMutation.isPending}
        patientId={patientId}
        onClose={invoiceModal.onClose}
        onSubmit={(body) => createInvoiceMutation.mutate(body)}
      />
    </VStack>
  );
}
